import { evaluatePasswordStrength } from '@shared/index';
import { cn } from '@/lib/cn';

interface Props {
  value: string;
  className?: string;
}

const BAR: Record<
  ReturnType<typeof evaluatePasswordStrength>['level'],
  string
> = {
  empty: 'bg-line',
  too_short: 'bg-danger-fill',
  easy: 'bg-warning-fill',
  medium: 'bg-action',
  strong: 'bg-success-fill',
};

const LABEL: Record<
  ReturnType<typeof evaluatePasswordStrength>['level'],
  string
> = {
  empty: 'text-fg-muted',
  too_short: 'text-danger',
  easy: 'text-warning',
  medium: 'text-action',
  strong: 'text-success',
};

/**
 * Live password strength with an animated meter (easy → strong).
 */
export function PasswordStrengthMeter({ value, className }: Props) {
  const strength = evaluatePasswordStrength(value);
  if (strength.level === 'empty') return null;

  const pulseStrong = strength.level === 'strong';

  return (
    <div
      className={cn('space-y-2', className)}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <div className="flex gap-1" aria-hidden>
        {[0, 1, 2, 3].map((segment) => {
          const filled = strength.percent >= (segment + 1) * 25;
          return (
            <div
              key={segment}
              className="h-1.5 flex-1 overflow-hidden rounded-full bg-line"
            >
              <div
                className={cn(
                  'h-full rounded-full transition-all duration-500 ease-out',
                  BAR[strength.level],
                  filled ? 'w-full opacity-100' : 'w-0 opacity-0',
                  pulseStrong && filled && 'motion-safe:animate-pulse',
                )}
              />
            </div>
          );
        })}
      </div>
      <p
        className={cn(
          'text-caption font-medium transition-all duration-300',
          LABEL[strength.level],
          pulseStrong && 'motion-safe:scale-[1.02] motion-safe:origin-left',
        )}
      >
        {strength.label}
      </p>
    </div>
  );
}
